import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Loader2, ShieldCheck, Calendar, MapPin, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/partner-invite/public/${token}`);
        setData(res.data);
        setForm((f) => ({ ...f, name: res.data.partnerName || '' }));
      } catch (e) {
        setError(e?.response?.data?.detail || 'Invalid or expired invite');
      }
    })();
  }, [token]);

  const claim = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data: res } = await axios.post(`${API}/partner-invite/claim/${token}`, {
        password: form.password,
        name: form.name,
      });
      // Stash the new JWT and route to dashboard.
      setSession(res.user, res.token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center p-6 text-center">
        <div>
          <Heart className="w-8 h-8 text-[#7B2C3B] mx-auto" />
          <h1 className="font-serif text-3xl mt-3">{error}</h1>
          <Link to="/" className="mt-5 inline-block text-sm text-[#7B2C3B] hover:underline">Back to Yourweddingfriends</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#7B2C3B]" /></div>;
  }

  const inviter = data.inviter || {};
  const inviterName = inviter.partner1 || 'Your partner';
  const coupleLabel = [inviter.partner1, inviter.partner2].filter(Boolean).join(' & ') || inviterName;
  const dateLabel = inviter.weddingDate ? new Date(inviter.weddingDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-[#FBF7F2]" data-testid="partner-invite">
      <header className="px-6 lg:px-10 pt-6 pb-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#7B2C3B] flex items-center justify-center">
            <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
          </div>
          <span className="font-serif text-base font-semibold">Yourwedding<span className="italic text-[#7B2C3B]">friends</span></span>
        </Link>
        <span className="inline-flex items-center gap-1.5 text-xs text-[#6A6A6A]">
          <ShieldCheck className="w-3.5 h-3.5 text-[#7B2C3B]" /> Private invite
        </span>
      </header>

      <div className="px-6 lg:px-10 py-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-7 animate-float-up">
            <p className="section-eyebrow">An invitation</p>
            <h1 className="font-serif text-4xl mt-2 text-[#2A2A2A]">
              {inviterName} invited you to plan together.
            </h1>
            <p className="text-sm text-[#6A6A6A] mt-3 max-w-sm mx-auto">
              You&apos;ll get your own login to the same private wedding workspace — budget, guests, vendors, the whole thing — and edit anything {inviterName} can.
            </p>
            <div className="mt-5 inline-flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-white border border-[#EFE3D8]">
              <p className="font-serif text-2xl text-[#2A2A2A]">{coupleLabel}</p>
              {dateLabel && (
                <p className="text-xs text-[#6A6A6A] inline-flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> {dateLabel}
                  {inviter.location && (<><span className="mx-1">·</span><MapPin className="w-3 h-3" /> {inviter.location}</>)}
                </p>
              )}
            </div>
          </div>

          {data.accepted ? (
            <div className="soft-card rounded-3xl bg-white border border-[#EFE3D8] p-8 text-center">
              <p className="text-sm text-[#6A6A6A]">This invite was already accepted. Please log in instead.</p>
              <Link to="/auth" className="mt-4 inline-flex items-center gap-2 btn-primary px-5 py-2.5 rounded-full text-sm">
                Sign in <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={claim} className="soft-card rounded-3xl bg-white border border-[#EFE3D8] p-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Your name</label>
                <input
                  data-testid="partner-claim-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Jordan"
                  required
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Your email</label>
                <input
                  value={data.partnerEmail}
                  disabled
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-[#FBF7F2] text-sm text-[#6A6A6A]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Pick a password</label>
                <input
                  data-testid="partner-claim-password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <button
                data-testid="partner-claim-submit"
                type="submit"
                disabled={submitting}
                className="w-full btn-primary py-3 rounded-full text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {submitting ? 'Joining…' : (<>Join {inviterName}&apos;s wedding <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <p className="text-[11px] text-center text-[#9A8A7E] inline-flex items-center justify-center gap-1.5 w-full">
                <ShieldCheck className="w-3 h-3" /> Same private workspace. We never sell your data.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
